import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportPoisVisitedDto } from "@report/data/dto/report-pois-visited.dto.ts";
import { ReportPoisVisitedDtoSchema } from "@report/data/dto/report-pois-visited.dto.ts";
import { Report } from "@report/domain/models/report.model";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportPoisVisitedMapper {
    map(
        reportPoisVisitedDto: ReportPoisVisitedDto,
    ): Result<Report[], ModelError<typeof Report>> {
        const reportPoisVisitedDtoParsed =
            ReportPoisVisitedDtoSchema.safeParse(reportPoisVisitedDto);

        if (!reportPoisVisitedDtoParsed.success) {
            console.error(reportPoisVisitedDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const targetStations = reportPoisVisitedDto.target_stations;

        const reportsResults = Object.values(reportPoisVisitedDto.data).map(
            (report) => {
                const station = report.station;
                const reportData = report.data;

                const reportMapped = reportData.map((reportDataValues) => ({
                    stationName:
                        targetStations[reportDataValues.station_id]?.name ?? "",
                    stationId: reportDataValues.station_id,
                    from: reportDataValues.from,
                    to: reportDataValues.to ?? 0,
                    time: Number(reportDataValues.time) || 0,
                }));

                return Report.createWithRecovery({
                    stationReportSummary: {
                        icon: station.icon,
                        stationId: station.id,
                        stationName: `${station.name} (${station.technical_name})`,
                    },
                    items: reportMapped,
                    pagination: {
                        count: 0,
                        current: 0,
                        pages: 0,
                        perPage: 0,
                    },
                });
            },
        );

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
